import type { UnitCobranza } from '@yawi/types';
import { Building2, Check, Info, Loader2, Lock, LockOpen, X } from 'lucide-react';
import React from 'react';
import { formatCurrency } from '../utils/format';

export interface AccessConfirmState {
  units: UnitCobranza[];
  block: boolean;
}

interface AccessConfirmModalProps {
  state: AccessConfirmState | null;
  loading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

// El bloqueo por cobranza es TOTAL: solo sobreviven consultar y pagar adeudos.
const BLOCK_RESTRICTIONS = [
  'Acceso personal del residente (QR, PIN y entrada en auto)',
  'Invitaciones, reservas de amenidades y apertura de puertas',
  'Comunicados, encuestas, documentos y proyectos',
  'Reportes, mascotas, vehículos y placas',
];

const PREVIEW_LIMIT = 4;

export const AccessConfirmModal: React.FC<AccessConfirmModalProps> = ({
  state,
  loading,
  onConfirm,
  onClose,
}) => {
  if (!state) return null;

  const { units, block } = state;
  const count = units.length;
  const preview = units.slice(0, PREVIEW_LIMIT);
  const extra = count - preview.length;

  const subtitle = block
    ? `Vas a restringir el acceso a ${count} unidad${count === 1 ? '' : 'es'}: solo podrán consultar y pagar sus adeudos.`
    : count === 1
      ? `Vas a restablecer el acceso completo a la unidad ${units[0].unit}.`
      : `Vas a restablecer el acceso completo a ${count} unidades.`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-xl ${
            block ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-500'
          }`}
        >
          {block ? <Lock size={22} /> : <LockOpen size={22} />}
        </div>

        <h2 className="mt-5 text-lg font-bold text-slate-800">
          {block ? '¿Bloquear acceso?' : '¿Habilitar acceso?'}
        </h2>
        <p className="mt-2 text-sm text-slate-500">{subtitle}</p>

        <div className="mt-5 space-y-3 rounded-2xl bg-slate-50 p-4">
          {preview.map((u) => (
            <div key={u.id} className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-500">
                <Building2 size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-800">{u.unit}</p>
                <p className="truncate text-xs text-slate-400">
                  {u.residents.map((r) => r.name).join(', ')}
                </p>
              </div>
              <span
                className={`shrink-0 text-sm font-semibold ${u.debtAmount > 0 ? 'text-red-600' : 'text-slate-400'}`}
              >
                {u.debtAmount > 0 ? formatCurrency(u.debtAmount) : '—'}
              </span>
            </div>
          ))}
          {extra > 0 && <p className="pt-1 text-xs font-medium text-slate-400">+ {extra} más</p>}
        </div>

        {block && (
          <div className="mt-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Al bloquear, dejan de poder
            </p>
            <ul className="mt-2 space-y-1.5">
              {BLOCK_RESTRICTIONS.map((label) => (
                <li key={label} className="flex items-center gap-2 text-sm text-slate-600">
                  <X size={16} className="shrink-0 text-red-500" />
                  {label}
                </li>
              ))}
            </ul>
          </div>
        )}

        {block ? (
          <div className="mt-5 flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
            <Info size={16} className="mt-0.5 shrink-0" />
            <p>
              El residente solo podrá consultar y pagar sus adeudos; el backend rechaza cualquier
              otra petición. Podrás revertirlo en cualquier momento y el acceso se restablece de
              inmediato.
            </p>
          </div>
        ) : (
          <div className="mt-5 flex items-start gap-2 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">
            <Check size={16} className="mt-0.5 shrink-0" />
            <p>
              Se restablece el acceso completo (acceso personal, invitaciones, amenidades y demás
              funciones) de inmediato.
            </p>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 cursor-pointer rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-60 ${
              block ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'
            }`}
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            {block ? 'Sí, bloquear' : 'Sí, habilitar'}
          </button>
        </div>
      </div>
    </div>
  );
};
